import React, { useState } from 'react';
import type { ProductAddon, SelectedAddon } from '../types';
import { generatePersonalisationCode, addToCart, redirectToCart } from '../utils/cartUtils';
import type { CartAddItem } from '../utils/cartUtils';

// Removed custom HTML element declarations - no longer using buy-buttons or quantity-wrapper

interface AddonSelectorProps {
  addons: ProductAddon[];
  mainProductId: string;
  mainVariantId: string;
  apiUrl: string;
}

export const AddonSelector: React.FC<AddonSelectorProps> = ({
  addons,
  mainVariantId,
}) => {
  const [selectedAddons, setSelectedAddons] = useState<Map<string, SelectedAddon>>(
    new Map()
  );
  const [metadataValues, setMetadataValues] = useState<
    Map<string, Record<string, string>>
  >(new Map());
  const [addingToCart, setAddingToCart] = useState(false);
  const [mainQuantity, setMainQuantity] = useState(1);

  const sanitizeTextfieldValue = (
    field: ProductAddon['metadata_fields'][number],
    value: string
  ): string => {
    let next = value;

    if (field.remove_special_chars) {
      next = next.replace(/[^a-zA-Z0-9 ]+/g, '');
    }
    if (field.remove_numbers) {
      next = next.replace(/[0-9]+/g, '');
    }

    const maxLength =
      typeof field.max_length === 'number' && field.max_length > 0
        ? field.max_length
        : null;
    if (maxLength) {
      next = next.slice(0, maxLength);
    }

    return next;
  };

  // Format price to RMxx.xx format
  const formatPrice = (price: string | number): string => {
    if (!price) return 'RM0.00';
    
    // Remove any currency symbols and whitespace
    const priceStr = String(price).replace(/[^\d.]/g, '');
    const numPrice = parseFloat(priceStr);
    
    if (isNaN(numPrice)) return 'RM0.00';
    
    // Format to 2 decimal places
    return `RM${numPrice.toFixed(2)}`;
  };

  const handleAddonToggle = (
    addonId: string,
    productId: string,
    variantId: string
  ) => {
    const key = `${addonId}-${productId}`;
    const current = selectedAddons.get(key);

    if (current) {
      // Remove addon
      const newMap = new Map(selectedAddons);
      newMap.delete(key);
      setSelectedAddons(newMap);

      // Clear metadata values for this addon
      const newMetadata = new Map(metadataValues);
      newMetadata.delete(key);
      setMetadataValues(newMetadata);
    } else {
      // Add addon (always quantity 1 for addons)
      const newMap = new Map(selectedAddons);
      newMap.set(key, {
        addonId,
        productId,
        variantId,
        quantity: 1, // Addons are always quantity 1
        metadata: {},
      });
      setSelectedAddons(newMap);
    }
  };

  const handleVariantChange = (
    addonId: string,
    productId: string,
    variantId: string
  ) => {
    const key = `${addonId}-${productId}`;
    const current = selectedAddons.get(key);
    if (current) {
      const newMap = new Map(selectedAddons);
      newMap.set(key, { ...current, variantId });
      setSelectedAddons(newMap);
    }
  };

  const handleMetadataChange = (
    addonId: string,
    productId: string,
    fieldName: string,
    value: string
  ) => {
    const key = `${addonId}-${productId}`;
    const current = selectedAddons.get(key);
    if (current) {
      const newMetadata = new Map(metadataValues);
      const addonMetadata = newMetadata.get(key) || {};
      addonMetadata[fieldName] = value;
      newMetadata.set(key, addonMetadata);
      setMetadataValues(newMetadata);

      // Update selected addon metadata
      const newMap = new Map(selectedAddons);
      newMap.set(key, { ...current, metadata: addonMetadata });
      setSelectedAddons(newMap);
    }
  };

  const handleAddToCart = async () => {
    // Addons are optional - can add main product with or without addons
    // Generate personalisation code (same for all items added together)
    const personalisationCode = generatePersonalisationCode();

    // Build cart items
    const cartItems: CartAddItem[] = [];

    // Add main product with personalisation code
    cartItems.push({
      id: mainVariantId,
      quantity: mainQuantity,
      properties: {
        Personalisation: personalisationCode,
      },
    });

    // Validate required metadata fields for selected addons
    for (const addon of addons) {
      for (const selectedAddon of selectedAddons.values()) {
        if (selectedAddon.addonId === addon.id) {
          for (const field of addon.metadata_fields) {
            if (field.required) {
              const key = `${selectedAddon.addonId}-${selectedAddon.productId}`;
              const metadata = metadataValues.get(key) || {};
              if (!metadata[field.field_name]?.trim()) {
                alert(`Please fill in the required field: ${field.field_name}`);
                return;
              }
            }
          }
        }
      }
    }

    try {
      setAddingToCart(true);

      // Add selected addon products
      for (const selectedAddon of selectedAddons.values()) {
        const addon = addons.find((a) => a.id === selectedAddon.addonId);
        if (!addon) continue;

        const properties: Record<string, string> = {
          Personalisation: personalisationCode,
          Addon: 'Yes',
        };

        // Add custom metadata fields
        const key = `${selectedAddon.addonId}-${selectedAddon.productId}`;
        const metadata = metadataValues.get(key) || {};
        Object.entries(metadata).forEach(([fieldName, value]) => {
          if (value.trim()) {
            properties[fieldName] = value;
          }
        });

        cartItems.push({
          id: selectedAddon.variantId,
          quantity: selectedAddon.quantity,
          properties,
        });
      }

      // Add to cart
      await addToCart(cartItems);

      // Redirect to cart
      redirectToCart();
    } catch (error: any) {
      console.error('Failed to add to cart:', error);
      alert(error.message || 'Failed to add items to cart');
    } finally {
      setAddingToCart(false);
    }
  };

  return (
    <div className="intan-addon-selector" style={{ padding: '16px 0' }}>
      {addons.length > 0 && (
        <div style={{ borderTop: '1px solid #e5e7eb', paddingTop: '24px' }}>
          <h3
            style={{
              fontSize: '20px',
              fontWeight: '600',
              color: '#111827',
              marginBottom: '20px',
              padding: '0 16px',
            }}
          >
            Available Addons
          </h3>

        {addons.map((addon) => (
          <div
            key={addon.id}
            style={{
              marginBottom: '24px',
              padding: '16px',
              border: '1px solid #e5e7eb',
              borderRadius: '8px',
            }}
          >
            <h4
              style={{
                fontSize: '16px',
                fontWeight: '500',
                color: '#111827',
                marginBottom: '16px',
              }}
            >
              {addon.name}
            </h4>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {addon.items.map((product, productIndex) => {
                const key = `${addon.id}-${product.id}`;
                const selectedAddon = selectedAddons.get(key);
                const isSelected = !!selectedAddon;
                const hasMultipleVariants = product.variants.length > 1;
                const selectedVariant = isSelected
                  ? product.variants.find(
                      (v) => v.id === selectedAddon.variantId
                    ) || product.variants[0]
                  : product.variants[0];
                const isLastItem = productIndex === addon.items.length - 1;

                // Use product options (available options) if available, otherwise extract from variants
                const options = product.options && product.options.length > 0
                  ? product.options.map((opt) => ({
                      name: opt.name,
                      values: opt.values || [],
                    }))
                  : (() => {
                      // Fallback: extract from variants if options not available
                      const optionMap = new Map<string, Set<string>>();
                      product.variants.forEach((variant) => {
                        if (variant.selectedOptions) {
                          variant.selectedOptions.forEach((option) => {
                            if (!optionMap.has(option.name)) {
                              optionMap.set(option.name, new Set());
                            }
                            optionMap.get(option.name)!.add(option.value);
                          });
                        }
                      });
                      return Array.from(optionMap.entries()).map(([name, values]) => ({
                        name,
                        values: Array.from(values),
                      }));
                    })();

                return (
                  <div
                    key={product.id}
                    style={{
                      padding: '16px',
                      ...(isSelected ? {
                        border: '2px solid #6f1920',
                        borderRadius: '8px',
                        backgroundColor: '#fafafa',
                      } : {}),
                      ...(isLastItem ? {} : {
                        borderBottom: isSelected ? 'none' : '1px solid #e5e7eb',
                        paddingBottom: '16px',
                        marginBottom: '16px',
                      }),
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '12px',
                      }}
                    >
                      {/* Product Image and Title - Mobile Stack */}
                      <div
                        style={{
                          display: 'flex',
                          gap: '12px',
                          alignItems: 'flex-start',
                        }}
                      >
                        {product.images.length > 0 && (
                          <img
                            src={product.images[0].src}
                            alt={product.images[0].alt || product.title}
                            style={{
                              width: '80px',
                              height: '80px',
                              objectFit: 'cover',
                              borderRadius: '8px',
                              flexShrink: 0,
                            }}
                          />
                        )}
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <h5
                            style={{
                              fontSize: '16px',
                              fontWeight: '500',
                              color: '#111827',
                              marginBottom: '4px',
                              wordBreak: 'break-word',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '8px',
                            }}
                          >
                            {product.title}
                            {isSelected && (
                              <span style={{ color: '#6f1920', fontSize: '18px', lineHeight: '1' }}>✓</span>
                            )}
                          </h5>
                          {selectedVariant && (
                            <p
                              style={{
                                fontSize: '14px',
                                color: '#000000',
                                fontWeight: '500',
                              }}
                            >
                              {formatPrice(selectedVariant.price)}
                            </p>
                          )}
                        </div>
                      </div>

                      {/* Option Selectors */}
                      {hasMultipleVariants && options.length > 0 && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                          {options.map((option) => {
                            // Get current selected value for this option
                            const currentValue = selectedVariant?.selectedOptions?.find(
                              (opt) => opt.name === option.name
                            )?.value || option.values[0];

                            return (
                              <div key={option.name}>
                                <label
                                  style={{
                                    display: 'block',
                                    fontSize: '14px',
                                    fontWeight: '500',
                                    color: '#374151',
                                    marginBottom: '8px',
                                  }}
                                >
                                  {option.name}
                                </label>
                                <select
                                  value={currentValue}
                                  onChange={(e) => {
                                    const newValue = e.target.value;
                                    
                                    // Find variant that matches all selected options
                                    const currentOptions = selectedVariant?.selectedOptions || [];
                                    const updatedOptions = currentOptions.map((opt) =>
                                      opt.name === option.name
                                        ? { name: option.name, value: newValue }
                                        : opt
                                    );
                                    
                                    // Find matching variant
                                    const matchingVariant = product.variants.find((variant) => {
                                      if (!variant.selectedOptions) return false;
                                      return updatedOptions.every((opt) =>
                                        variant.selectedOptions?.some(
                                          (vOpt) => vOpt.name === opt.name && vOpt.value === opt.value
                                        )
                                      );
                                    });

                                    if (matchingVariant) {
                                      if (isSelected) {
                                        handleVariantChange(
                                          addon.id,
                                          product.id,
                                          matchingVariant.id
                                        );
                                      } else {
                                        handleAddonToggle(
                                          addon.id,
                                          product.id,
                                          matchingVariant.id
                                        );
                                      }
                                    }
                                  }}
                                  style={{
                                    width: '100%',
                                    borderBottom: '1px solid #e5e7eb',
                                    borderTop: 'none',
                                    borderLeft: 'none',
                                    borderRight: 'none',
                                    borderRadius: '0',
                                    padding: '8px 0',
                                    fontSize: '16px',
                                    backgroundColor: 'transparent',
                                    minHeight: '44px', // Mobile touch target
                                  }}
                                >
                                  {option.values.map((value) => (
                                    <option key={value} value={value}>
                                      {value}
                                    </option>
                                  ))}
                                </select>
                              </div>
                            );
                          })}
                        </div>
                      )}


                      {/* Metadata Fields */}
                      {isSelected &&
                        addon.metadata_fields
                          .sort((a, b) => a.display_order - b.display_order)
                          .map((field) => {
                            const metadataKey = `${addon.id}-${product.id}`;
                            const metadata = metadataValues.get(metadataKey) || {};
                            const value = metadata[field.field_name] || '';

                            return (
                              <div key={field.id}>
                                <label
                                  style={{
                                    display: 'block',
                                    fontSize: '14px',
                                    fontWeight: '500',
                                    color: '#374151',
                                    marginBottom: '8px',
                                  }}
                                >
                                  {field.field_name}
                                  {field.required && (
                                    <span style={{ color: '#ef4444', marginLeft: '4px' }}>
                                      *
                                    </span>
                                  )}
                                </label>
                                {field.field_type === 'textfield' ? (
                                  <input
                                    type="text"
                                    value={value}
                                    onChange={(e) =>
                                      handleMetadataChange(
                                        addon.id,
                                        product.id,
                                        field.field_name,
                                        sanitizeTextfieldValue(field, e.target.value)
                                      )
                                    }
                                    required={field.required}
                                    maxLength={
                                      typeof field.max_length === 'number' && field.max_length > 0
                                        ? field.max_length
                                        : undefined
                                    }
                                    style={{
                                      width: '100%',
                                      borderBottom: '1px solid #e5e7eb',
                                      borderTop: 'none',
                                      borderLeft: 'none',
                                      borderRight: 'none',
                                      borderRadius: '0',
                                      padding: '8px 0',
                                      fontSize: '16px',
                                      backgroundColor: 'transparent',
                                      minHeight: '44px', // Mobile touch target
                                    }}
                                  />
                                ) : (
                                  <select
                                    value={value}
                                    onChange={(e) =>
                                      handleMetadataChange(
                                        addon.id,
                                        product.id,
                                        field.field_name,
                                        e.target.value
                                      )
                                    }
                                    required={field.required}
                                    style={{
                                      width: '100%',
                                      borderBottom: '1px solid #e5e7eb',
                                      borderTop: 'none',
                                      borderLeft: 'none',
                                      borderRight: 'none',
                                      borderRadius: '0',
                                      padding: '8px 0',
                                      fontSize: '16px',
                                      backgroundColor: 'transparent',
                                      minHeight: '44px', // Mobile touch target
                                    }}
                                  >
                                    <option value="">Select...</option>
                                    {field.field_options.map((option) => (
                                      <option key={option} value={option}>
                                        {option}
                                      </option>
                                    ))}
                                  </select>
                                )}
                              </div>
                            );
                          })}

                      {/* Toggle Button - Add or Remove */}
                      <div style={{ marginTop: '12px' }}>
                        {isSelected ? (
                          <button
                            type="button"
                            onClick={() => {
                              handleAddonToggle(addon.id, product.id, '');
                            }}
                            style={{
                              width: '100%',
                              padding: '14px 20px',
                              borderRadius: '6px',
                              fontSize: '16px',
                              fontWeight: '500',
                              border: 'none',
                              cursor: 'pointer',
                              minHeight: '48px', // Mobile touch target
                              backgroundColor: 'transparent',
                              color: '#df5641',
                              transition: 'opacity 0.2s',
                              textAlign: 'center',
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.opacity = '0.8';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.opacity = '1';
                            }}
                          >
                            Remove <span style={{ fontSize: '18px', lineHeight: '1' }}>×</span>
                          </button>
                        ) : (
                          <button
                            type="button"
                            onClick={() => {
                              // Auto-select first variant
                              const variantId = product.variants[0]?.id || '';
                              handleAddonToggle(addon.id, product.id, variantId);
                            }}
                            style={{
                              width: '100%',
                              padding: '14px 20px',
                              borderRadius: '6px',
                              fontSize: '16px',
                              fontWeight: '500',
                              border: 'none',
                              cursor: 'pointer',
                              minHeight: '48px', // Mobile touch target
                              backgroundColor: '#000000',
                              color: '#ffffff',
                              transition: 'opacity 0.2s',
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.opacity = '0.8';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.opacity = '1';
                            }}
                          >
                            Add
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
        </div>
      )}

      {/* Quantity Selector and Add to Cart Button - Without form/buy-buttons wrapper to avoid conflicting with Shopify's variant picker */}
      <div style={{ marginTop: '24px' }}>
        <div className="quantity-submit-row input-row">
          <label className="label" htmlFor="intan-quantity" style={{ display: 'block', marginBottom: '8px', fontSize: '14px' }}>
            Quantity
          </label>
          <div
            className="quantity-wrapper"
            style={{
              width: '358px',
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <button
              type="button"
              className="rp-quantity-button-input"
              onClick={(e) => {
                e.preventDefault();
                setMainQuantity((prev) => Math.max(1, prev - 1));
              }}
              data-quantity="down"
              aria-label="Decrease quantity"
              style={{
                width: '48px',
                height: '48px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: '1px solid #dbdcdc',
                color: 'currentColor',
                backgroundColor: '#fff',
                borderRadius: '0',
                fontSize: '14px',
                cursor: 'pointer',
                padding: '0',
              }}
            >
              -
            </button>
            <input
              aria-label="Quantity"
              id="intan-quantity"
              type="number"
              name="quantity"
              value={mainQuantity}
              onChange={(e) => {
                const value = parseInt(e.target.value) || 1;
                setMainQuantity(Math.max(1, value));
              }}
              style={{
                width: '48px',
                height: '48px',
                textAlign: 'center',
                border: '1px solid #dbdcdc',
                padding: '0',
                fontSize: '14px',
                borderRadius: '0',
              }}
            />
            <button
              type="button"
              className="rp-quantity-button-input"
              onClick={(e) => {
                e.preventDefault();
                setMainQuantity((prev) => prev + 1);
              }}
              data-quantity="up"
              aria-label="Increase quantity"
              style={{
                width: '48px',
                height: '48px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: '1px solid #dbdcdc',
                color: 'currentColor',
                backgroundColor: '#fff',
                borderRadius: '0',
                fontSize: '14px',
                cursor: 'pointer',
                padding: '0',
              }}
            >
              +
            </button>
          </div>

          <div className="quantity-submit-row__submit input-row" style={{ marginTop: '16px', width: '100%' }}>
            <button
              className="btn btn--large add-to-cart"
              type="button"
              onClick={handleAddToCart}
              disabled={addingToCart}
              data-add-to-cart-text="Add to cart"
              style={{
                width: '100%',
                padding: '14px 20px',
                borderRadius: '6px',
                fontSize: '14px',
                fontWeight: '500',
                border: 'none',
                cursor: addingToCart ? 'not-allowed' : 'pointer',
                backgroundColor: addingToCart ? '#9ca3af' : '#6f1920',
                color: '#ffffff',
                transition: 'opacity 0.2s',
                minHeight: '48px',
              }}
              onMouseEnter={(e) => {
                if (!addingToCart) {
                  e.currentTarget.style.opacity = '0.8';
                }
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.opacity = '1';
              }}
            >
          {addingToCart ? (
            <span style={{
              display: 'inline-block',
              width: '16px',
              height: '16px',
              border: '2px solid rgba(255, 255, 255, 0.3)',
              borderTop: '2px solid #ffffff',
              borderRadius: '50%',
              animation: 'spin 0.8s linear infinite',
              verticalAlign: 'middle',
            }}></span>
          ) : (
            'Add to cart'
          )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

